// EnterpriseSupport.jsx — src/components/EnterpriseSupport.jsx
// COMPLETELY STANDALONE — no forwardRef, no complex state sync, no external deps

import React, { useState, useEffect, useRef, useCallback } from "react";

const SUPPORT_URL = "https://pilocoustomersupport.onrender.com";

// ── Auth ───────────────────────────────────────────────────────────────────────
const ensureAuth = async (userId) => {
  const key = "pb_support_token";
  const existing = localStorage.getItem(key);
  if (existing) {
    try {
      const r = await fetch(`${SUPPORT_URL}/tickets?page=1&page_size=1`, {
        headers: { Authorization: `Bearer ${existing}` },
      });
      if (r.status !== 401) return existing;
    } catch (_) {}
    localStorage.removeItem(key);
  }

  const email = `pbuser_${userId}@producerbuddy.com`;
  const password = `PB_${userId}_customer2024`;

  // Try login
  try {
    const r = await fetch(`${SUPPORT_URL}/auth/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password }),
    });
    if (r.ok) { const d = await r.json(); localStorage.setItem(key, d.access_token); return d.access_token; }
  } catch (_) {}

  // Register then login
  try {
    await fetch(`${SUPPORT_URL}/auth/register`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password, role: "customer" }),
    });
    const r = await fetch(`${SUPPORT_URL}/auth/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password }),
    });
    if (r.ok) { const d = await r.json(); localStorage.setItem(key, d.access_token); return d.access_token; }
  } catch (_) {}

  return null;
};

const getHeaders = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${localStorage.getItem("pb_support_token")}`,
});

// ── API calls ──────────────────────────────────────────────────────────────────
const apiCreateTicket = async (subject, message) => {
  const r = await fetch(`${SUPPORT_URL}/tickets/create`, {
    method: "POST",
    headers: getHeaders(),
    body: JSON.stringify({ category: "contact_sales", subject, initial_message: message }),
  });
  if (!r.ok) throw new Error(`Failed to create ticket: ${r.status}`);
  return r.json();
};

const apiGetMessages = async (ticketId) => {
  const r = await fetch(`${SUPPORT_URL}/tickets/${ticketId}/messages`, { headers: getHeaders() });
  if (!r.ok) return [];
  const d = await r.json();
  return Array.isArray(d) ? d : [];
};

const apiSendMessage = async (ticketId, text) => {
  const r = await fetch(`${SUPPORT_URL}/tickets/${ticketId}/messages`, {
    method: "POST",
    headers: getHeaders(),
    body: JSON.stringify({ message_text: text }),
  });
  return r.json();
};

const findPaymentLink = (text) => {
  if (!text) return null;
  const m = text.match(/https?:\/\/\S+/g);
  if (!m) return null;
  return m.find(u => u.includes("/payment/") || u.includes("piloment")) || null;
};

// =============================================================================
// MAIN COMPONENT
// =============================================================================
const EnterpriseSupport = ({ isOpen, onClose, user, selectedPlan, selectedTools, context, onPaymentRedirect }) => {
  const [visible, setVisible]       = useState(false);
  const [phase, setPhase]           = useState("idle"); // idle | loading | chat | error
  const [ticket, setTicket]         = useState(null);
  const [messages, setMessages]     = useState([]);
  const [input, setInput]           = useState("");
  const [sending, setSending]       = useState(false);
  const [errorMsg, setErrorMsg]     = useState("");
  const [payLink, setPayLink]       = useState(null);
  const [unread, setUnread]         = useState(0);

  const pollTimer  = useRef(null);
  const bottomRef  = useRef(null);
  const startedRef = useRef(false); // prevent double-start

  // ── Open/close sync with parent ─────────────────────────────────────────────
  useEffect(() => {
    if (isOpen) {
      setVisible(true);
    }
  }, [isOpen]);

  // When visible opens fresh, start the conversation
  useEffect(() => {
    if (visible && phase === "idle" && !startedRef.current) {
      startedRef.current = true;
      doConnect();
    }
  }, [visible]); // eslint-disable-line

  // ── Unread badge when closed ────────────────────────────────────────────────
  useEffect(() => {
    if (!visible) {
      setUnread(messages.filter(m => m.sender_type === "agent").length);
    } else {
      setUnread(0);
    }
  }, [messages, visible]);

  // ── Auto scroll ─────────────────────────────────────────────────────────────
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // ── Poll ────────────────────────────────────────────────────────────────────
  const poll = useCallback(async (id) => {
    try {
      const msgs = await apiGetMessages(id);
      setMessages(msgs);
      for (const m of msgs.filter(x => x.sender_type === "agent")) {
        const link = findPaymentLink(m.message_text);
        if (link) { setPayLink(link); break; }
      }
    } catch (_) {}
  }, []);

  useEffect(() => {
    if (phase === "chat" && ticket?.id) {
      poll(ticket.id);
      pollTimer.current = setInterval(() => poll(ticket.id), 4000);
      return () => clearInterval(pollTimer.current);
    }
    return () => clearInterval(pollTimer.current);
  }, [phase, ticket, poll]);

  // ── Connect ─────────────────────────────────────────────────────────────────
  const doConnect = async () => {
    const uid = user?.user_id;
    if (!uid) {
      setErrorMsg("Please log in before using support.");
      setPhase("error");
      return;
    }

    setPhase("loading");
    setErrorMsg("");

    try {
      const token = await ensureAuth(uid);
      if (!token) throw new Error("Could not authenticate with support system");

      const parts = [
        "Hello! I need help with ProducerBuddy.",
        selectedPlan ? `Interested plan: ${selectedPlan}.` : "",
        selectedTools?.length ? `Selected tools: ${selectedTools.map(t => t.name).join(", ")}.` : "",
        context ? `Enquiry: ${context}.` : "",
        `My PB User ID: ${uid}.`,
      ].filter(Boolean);

      const subject = context
        ? `Enquiry — ${context}`
        : `Enterprise Request — ${selectedPlan || "Custom"}`;

      const t = await apiCreateTicket(subject, parts.join(" "));
      if (!t?.id) throw new Error("Ticket creation returned no ID");

      setTicket(t);
      setPhase("chat");
      await poll(t.id);
    } catch (err) {
      setErrorMsg(err.message || "Connection failed. Please try again.");
      setPhase("error");
    }
  };

  const retry = () => {
    startedRef.current = false;
    setPhase("idle");
    setErrorMsg("");
    startedRef.current = true;
    doConnect();
  };

  // ── Send ─────────────────────────────────────────────────────────────────────
  const handleSend = async () => {
    if (!input.trim() || !ticket?.id || sending) return;
    setSending(true);
    try {
      await apiSendMessage(ticket.id, input.trim());
      setInput("");
      await poll(ticket.id);
    } catch (_) {}
    finally { setSending(false); }
  };

  // ── Open from floating button ────────────────────────────────────────────────
  const openFromButton = () => {
    startedRef.current = false; // allow fresh start
    setPhase("idle");
    setVisible(true);
  };

  // ── Close ────────────────────────────────────────────────────────────────────
  const handleClose = () => {
    setVisible(false);
    startedRef.current = false; // allow re-opening fresh next time
    if (onClose) onClose();
  };

  return (
    <>
      {/* ── Floating button ── */}
      <button
        onClick={openFromButton}
        style={{
          position: "fixed",
          bottom: "24px",
          right: "24px",
          zIndex: 9999,
          display: "flex",
          alignItems: "center",
          gap: "8px",
          padding: "12px 18px",
          backgroundColor: "#2563eb",
          color: "white",
          border: "none",
          borderRadius: "9999px",
          fontWeight: 600,
          fontSize: "14px",
          cursor: "pointer",
          boxShadow: "0 8px 32px rgba(37,99,235,0.45)",
        }}
      >
        <svg width="18" height="18" fill="none" stroke="white" strokeWidth="2" viewBox="0 0 24 24">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
        </svg>
        Talk to our team
        {unread > 0 && (
          <span style={{ background: "#ef4444", borderRadius: "9999px", width: 20, height: 20, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700 }}>
            {unread}
          </span>
        )}
      </button>

      {/* ── Chat panel ── */}
      {visible && (
        <div style={{
          position: "fixed",
          bottom: "90px",
          right: "24px",
          zIndex: 9999,
          width: "384px",
          height: "520px",
          backgroundColor: "white",
          borderRadius: "16px",
          boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
          border: "1px solid #e5e7eb",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}>

          {/* Header */}
          <div style={{ background: "#2563eb", padding: "12px 16px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <div style={{ width: 36, height: 36, borderRadius: "50%", background: "rgba(255,255,255,0.2)", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 14, color: "white" }}>P</div>
              <div>
                <p style={{ fontWeight: 600, fontSize: 14, color: "white", margin: 0 }}>ProducerBuddy Support</p>
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 2 }}>
                  <div style={{ width: 6, height: 6, borderRadius: "50%", background: phase === "chat" ? "#4ade80" : phase === "loading" ? "#fbbf24" : "#9ca3af" }} />
                  <p style={{ fontSize: 11, color: "rgba(255,255,255,0.8)", margin: 0 }}>
                    {phase === "chat" ? "Connected" : phase === "loading" ? "Connecting..." : "Support"}
                  </p>
                </div>
              </div>
            </div>
            <button onClick={handleClose} style={{ background: "none", border: "none", cursor: "pointer", color: "rgba(255,255,255,0.8)", padding: 4 }}>
              <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          </div>

          {/* Body */}
          <div style={{ flex: 1, overflowY: "auto", padding: 16, background: "#f9fafb" }}>

            {/* LOADING */}
            {phase === "loading" && (
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%", gap: 12 }}>
                <div style={{ width: 36, height: 36, border: "3px solid #dbeafe", borderTopColor: "#2563eb", borderRadius: "50%", animation: "spin 1s linear infinite" }} />
                <p style={{ fontSize: 14, color: "#6b7280", margin: 0 }}>Connecting you to our team...</p>
                <p style={{ fontSize: 12, color: "#9ca3af", margin: 0 }}>Usually takes a few seconds</p>
                <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
              </div>
            )}

            {/* ERROR */}
            {phase === "error" && (
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%", gap: 16, padding: "0 16px" }}>
                <div style={{ width: 48, height: 48, borderRadius: "50%", background: "#fee2e2", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <svg width="22" height="22" fill="none" stroke="#ef4444" strokeWidth="2" viewBox="0 0 24 24">
                    <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                  </svg>
                </div>
                <p style={{ fontSize: 14, color: "#dc2626", textAlign: "center", margin: 0 }}>{errorMsg}</p>
                <button onClick={retry} style={{ padding: "10px 24px", background: "#2563eb", color: "white", border: "none", borderRadius: 10, fontWeight: 600, fontSize: 14, cursor: "pointer" }}>
                  Try again
                </button>
              </div>
            )}

            {/* IDLE (fallback) */}
            {phase === "idle" && (
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%", gap: 16, padding: "0 16px" }}>
                <div style={{ width: 56, height: 56, borderRadius: "50%", background: "#dbeafe", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <svg width="28" height="28" fill="none" stroke="#2563eb" strokeWidth="1.5" viewBox="0 0 24 24">
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                  </svg>
                </div>
                <div style={{ textAlign: "center" }}>
                  <p style={{ fontWeight: 600, fontSize: 15, color: "#1f2937", margin: 0 }}>Chat with our team</p>
                  <p style={{ fontSize: 13, color: "#6b7280", marginTop: 4 }}>We'll help you find the right plan</p>
                </div>
                <button onClick={doConnect} style={{ width: "100%", padding: "12px", background: "#2563eb", color: "white", border: "none", borderRadius: 12, fontWeight: 600, fontSize: 14, cursor: "pointer" }}>
                  Start conversation →
                </button>
              </div>
            )}

            {/* CHAT */}
            {phase === "chat" && (
              <>
                <div style={{ textAlign: "center", marginBottom: 16 }}>
                  <span style={{ background: "#dbeafe", color: "#1d4ed8", fontSize: 11, fontWeight: 600, padding: "4px 12px", borderRadius: 99 }}>
                    Connected · Ticket #{ticket?.id}
                  </span>
                </div>

                {/* Payment link banner */}
                {payLink && (
                  <div style={{ background: "#f0fdf4", border: "1px solid #86efac", borderRadius: 12, padding: 12, marginBottom: 16 }}>
                    <p style={{ fontSize: 12, fontWeight: 700, color: "#15803d", margin: "0 0 8px 0" }}>✅ Your payment link is ready!</p>
                    <button
                      onClick={() => onPaymentRedirect ? onPaymentRedirect(payLink) : (window.location.href = payLink)}
                      style={{ width: "100%", padding: "10px", background: "#16a34a", color: "white", border: "none", borderRadius: 8, fontWeight: 600, fontSize: 13, cursor: "pointer" }}
                    >
                      Complete Payment →
                    </button>
                  </div>
                )}

                {/* Messages */}
                {messages.map((msg, i) => {
                  const isAgent = msg.sender_type === "agent";
                  return (
                    <div key={i} style={{ display: "flex", justifyContent: isAgent ? "flex-start" : "flex-end", marginBottom: 12 }}>
                      {isAgent && (
                        <div style={{ width: 28, height: 28, borderRadius: "50%", background: "#2563eb", color: "white", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, marginRight: 8, alignSelf: "flex-end", marginBottom: 2 }}>P</div>
                      )}
                      <div style={{
                        maxWidth: "75%", padding: "8px 12px", borderRadius: 16,
                        background: isAgent ? "white" : "#2563eb",
                        color: isAgent ? "#1f2937" : "white",
                        border: isAgent ? "1px solid #f3f4f6" : "none",
                        boxShadow: isAgent ? "0 1px 4px rgba(0,0,0,0.08)" : "none",
                        borderBottomLeftRadius: isAgent ? 4 : 16,
                        borderBottomRightRadius: isAgent ? 16 : 4,
                      }}>
                        <p style={{ fontSize: 13, margin: 0, lineHeight: 1.5, whiteSpace: "pre-wrap" }}>{msg.message_text}</p>
                        <p style={{ fontSize: 10, margin: "4px 0 0 0", opacity: 0.6 }}>
                          {new Date(msg.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                        </p>
                      </div>
                    </div>
                  );
                })}

                {messages.length === 1 && (
                  <p style={{ textAlign: "center", fontSize: 11, color: "#9ca3af", padding: "12px 0" }}>
                    An agent will respond shortly · Avg wait: 2–5 mins
                  </p>
                )}
                <div ref={bottomRef} />
              </>
            )}
          </div>

          {/* Input */}
          {phase === "chat" && (
            <div style={{ padding: "10px 12px", background: "white", borderTop: "1px solid #f3f4f6" }}>
              <div style={{ display: "flex", gap: 8, alignItems: "flex-end", padding: 8, borderRadius: 12, background: "#f9fafb", border: "1px solid #e5e7eb" }}>
                <textarea
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
                  placeholder="Type a message… (Enter to send)"
                  rows={2}
                  style={{ flex: 1, background: "transparent", border: "none", outline: "none", resize: "none", fontSize: 13, color: "#1f2937", fontFamily: "inherit" }}
                />
                <button
                  onClick={handleSend}
                  disabled={sending || !input.trim()}
                  style={{ padding: 8, background: sending || !input.trim() ? "#93c5fd" : "#2563eb", border: "none", borderRadius: 10, cursor: sending || !input.trim() ? "not-allowed" : "pointer", display: "flex", alignItems: "center" }}
                >
                  {sending ? (
                    <div style={{ width: 16, height: 16, border: "2px solid white", borderTopColor: "transparent", borderRadius: "50%", animation: "spin 1s linear infinite" }} />
                  ) : (
                    <svg width="15" height="15" fill="none" stroke="white" strokeWidth="2" viewBox="0 0 24 24">
                      <line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/>
                    </svg>
                  )}
                </button>
              </div>
              <p style={{ fontSize: 10, color: "#9ca3af", textAlign: "center", margin: "6px 0 0 0" }}>
                Ticket #{ticket?.id} · Powered by Pilomation Support
              </p>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default EnterpriseSupport;
